import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Mic, PhoneOff } from 'lucide-react';
import api from '../../services/api';

// The server's media bridge speaks 8 kHz 16-bit PCM (base64) in JSON frames, same as the phone path.
const RATE = 8000;
const FRAME = 160; // 20 ms

const toB64 = (int16) => { let s = ''; const u8 = new Uint8Array(int16.buffer); for (let i = 0; i < u8.length; i++) s += String.fromCharCode(u8[i]); return btoa(s); };
const fromB64 = (b64) => { const bin = atob(b64); const u8 = new Uint8Array(bin.length); for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i); return new Int16Array(u8.buffer, 0, u8.length >> 1); };

const WORKLET = `class Tap extends AudioWorkletProcessor { process(i){ const c=i[0][0]; if(c) this.port.postMessage(c.slice(0)); return true; } } registerProcessor('tap', Tap);`;

export default function WebTestCall() {
  const [phase, setPhase] = useState('idle'); // idle | connecting | live | finished
  const [error, setError] = useState('');
  const [seconds, setSeconds] = useState(0);
  const [result, setResult] = useState(null);
  const r = useRef({});

  const cleanup = useCallback(() => {
    const s = r.current;
    clearInterval(s.timer);
    try { s.node?.disconnect(); } catch { /* already gone */ }
    s.stream?.getTracks().forEach((t) => t.stop());
    try { s.ctx?.close(); } catch { /* already closed */ }
    if (s.ws && s.ws.readyState <= 1) { try { s.ws.send(JSON.stringify({ event: 'stop' })); s.ws.close(); } catch { /* socket closing */ } }
    r.current = {};
  }, []);
  useEffect(() => cleanup, [cleanup]);

  const waitForResult = async (id) => {
    for (let i = 0; i < 15; i++) {
      await new Promise((res) => setTimeout(res, 2000));
      try {
        const { data } = await api.get(`/voice/calls/${id}`);
        const c = data.data || data;
        if (c.status === 'completed' && (c.summary || c.transcript === '' || i > 8)) return c;
      } catch { /* keep polling */ }
    }
    return null;
  };

  const end = useCallback(async () => {
    const id = r.current.callLogId;
    cleanup();
    setPhase('finished');
    setResult(null);
    if (id) setResult((await waitForResult(id)) || { notReady: true });
  }, [cleanup]);

  const start = async () => {
    setError(''); setResult(null); setSeconds(0); setPhase('connecting');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true } });
      const { data } = await api.post('/calling-agent/web-call');
      const ctx = new AudioContext();
      await ctx.audioWorklet.addModule(URL.createObjectURL(new Blob([WORKLET], { type: 'application/javascript' })));
      const ws = new WebSocket(data.streamUrl);
      r.current = { ws, ctx, stream, callLogId: data.callLogId, next: 0, carry: [] };

      ws.onopen = () => {
        ws.send(JSON.stringify({ event: 'start', start: { stream_sid: 'web', media_format: { sample_rate: '8000' } } }));
        const node = new AudioWorkletNode(ctx, 'tap');
        r.current.node = node;
        ctx.createMediaStreamSource(stream).connect(node);
        const ratio = ctx.sampleRate / RATE;
        node.port.onmessage = (e) => {
          // Average each window of input samples down to one 8 kHz sample.
          const input = e.data; const s = r.current;
          for (let i = 0; i < input.length; i++) {
            s.acc = (s.acc || 0) + input[i]; s.n = (s.n || 0) + 1;
            if (s.n >= ratio) { s.carry.push(Math.max(-1, Math.min(1, s.acc / s.n)) * 32767); s.acc = 0; s.n = 0; }
          }
          while (s.carry.length >= FRAME && ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ event: 'media', media: { payload: toB64(Int16Array.from(s.carry.splice(0, FRAME))) } }));
          }
        };
        r.current.timer = setInterval(() => setSeconds((x) => x + 1), 1000);
        setPhase('live');
      };
      ws.onmessage = (e) => {
        let msg; try { msg = JSON.parse(e.data); } catch { return; }
        if (msg.event === 'media' && msg.media?.payload) {
          const pcm = fromB64(msg.media.payload);
          const buf = ctx.createBuffer(1, pcm.length, RATE);
          const ch = buf.getChannelData(0);
          for (let i = 0; i < pcm.length; i++) ch[i] = pcm[i] / 32768;
          const src = ctx.createBufferSource(); src.buffer = buf; src.connect(ctx.destination);
          const s = r.current; s.next = Math.max(ctx.currentTime + 0.05, s.next || 0);
          src.start(s.next); s.next += buf.duration;
        }
      };
      ws.onerror = () => setError('Could not reach the voice server.');
      ws.onclose = () => { if (r.current.ws === ws) end(); };
    } catch (e) {
      cleanup(); setPhase('idle');
      setError(e.name === 'NotAllowedError' ? 'Allow microphone access to talk to the agent.' : (e.response?.data?.error || e.message || 'Could not start the test call'));
    }
  };

  const mmss = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 flex-wrap">
        {phase === 'live' ? (
          <button onClick={end} className="flex items-center gap-2 h-9 px-4 rounded bg-[#BA0517] hover:bg-[#8E030F] text-white text-[13px] font-semibold"><PhoneOff size={15} /> End call · {mmss}</button>
        ) : (
          <button onClick={start} disabled={phase === 'connecting'} className="flex items-center gap-2 h-9 px-4 rounded bg-[#2E844A] hover:bg-[#256B3C] disabled:opacity-60 text-white text-[13px] font-semibold"><Mic size={15} /> {phase === 'connecting' ? 'Connecting…' : 'Talk to the agent'}</button>
        )}
        <span className="text-[12px] text-[#706E6B]">Uses your microphone. Use headphones to avoid echo. Save your changes first: the test uses the saved settings.</span>
      </div>
      {error && <p className="text-[12px] text-[#BA0517]">{error}</p>}
      {phase === 'finished' && !result && <p className="text-[12px] text-[#706E6B]">Call ended. Analysing the conversation…</p>}
      {result?.notReady && <p className="text-[12px] text-[#706E6B]">The call is saved, but the summary is not ready yet. Check Voice → Calls in a moment.</p>}
      {result && !result.notReady && (
        <div className="bg-[#F7F9FB] border border-[#D8DDE6] rounded-lg p-3 space-y-2 text-[13px]">
          <div className="flex flex-wrap gap-x-6 gap-y-1">
            {result.disposition && <span><b>Outcome:</b> {result.disposition}</span>}
            {result.qa?.score && <span><b>QA:</b> {result.qa.score}/10{result.qa.notes ? ` · ${result.qa.notes}` : ''}</span>}
            {result.flowTrail?.length > 0 && <span><b>Steps:</b> {result.flowTrail.map((t) => t.step).join(' → ')}</span>}
          </div>
          {result.extracted && Object.keys(result.extracted).length > 0 && <div><b>Collected:</b> {Object.entries(result.extracted).map(([k, v]) => `${k}: ${v}`).join(' · ')}</div>}
          {result.summary && <div><b>Summary:</b> {result.summary}</div>}
          {result.transcript && <pre className="whitespace-pre-wrap text-[12px] text-[#444444] max-h-48 overflow-y-auto">{result.transcript}</pre>}
        </div>
      )}
    </div>
  );
}
